<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-h5">
            <v-icon class="mr-2">mdi-account-circle</v-icon>
            {{ $t('profile.title') }}
          </v-card-title>
          
          <v-card-text>
            <v-form ref="profileForm" v-model="valid" @submit.prevent="updateProfile">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="profileData.firstName"
                    :label="$t('profile.firstName')"
                    :rules="[v => !!v || $t('validation.required')]"
                    required
                    outlined
                  />
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="profileData.lastName"
                    :label="$t('profile.lastName')"
                    :rules="[v => !!v || $t('validation.required')]"
                    required
                    outlined
                  />
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="profileData.email"
                    :label="$t('profile.email')"
                    :rules="emailRules"
                    type="email"
                    required
                    outlined
                  />
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="profileData.phone"
                    :label="$t('profile.phone')"
                    :rules="phoneRules"
                    required
                    outlined
                    readonly
                  />
                </v-col>
                
                <v-col cols="12">
                  <v-select
                    v-model="profileData.role"
                    :items="roleOptions"
                    :label="$t('profile.role')"
                    outlined
                    readonly
                  />
                </v-col>
              </v-row>
              
              <v-divider class="my-4" />
              
              <v-row>
                <v-col cols="12">
                  <h3 class="text-h6 mb-3">{{ $t('profile.changePassword') }}</h3>
                </v-col>
                
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="passwordData.currentPassword"
                    :label="$t('profile.currentPassword')"
                    :type="showCurrentPassword ? 'text' : 'password'"
                    :append-icon="showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showCurrentPassword = !showCurrentPassword"
                    outlined
                  />
                </v-col>
                
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="passwordData.newPassword"
                    :label="$t('profile.newPassword')"
                    :type="showNewPassword ? 'text' : 'password'"
                    :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="passwordRules"
                    @click:append="showNewPassword = !showNewPassword"
                    outlined
                  />
                </v-col>
                
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="passwordData.confirmPassword"
                    :label="$t('profile.confirmPassword')"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="confirmPasswordRules"
                    @click:append="showConfirmPassword = !showConfirmPassword"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              :loading="loading"
              @click="updateProfile"
              :disabled="!valid"
            >
              {{ $t('profile.updateProfile') }}
            </v-btn>
            <v-btn
              color="secondary"
              :loading="passwordLoading"
              @click="changePassword"
              :disabled="!canChangePassword"
              class="ml-2"
            >
              {{ $t('profile.changePassword') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Admin Stats Card -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-h6">
            <v-icon class="mr-2">mdi-chart-line</v-icon>
            {{ $t('admin.statistics') }}
          </v-card-title>
          
          <v-card-text>
            <v-row>
              <v-col cols="6" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <v-icon color="primary" size="48">mdi-account-group</v-icon>
                    <div class="text-h4 primary--text">{{ stats.totalUsers }}</div>
                    <div class="text-caption">{{ $t('admin.totalUsers') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="6" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <v-icon color="success" size="48">mdi-account-check</v-icon>
                    <div class="text-h4 success--text">{{ stats.activeUsers }}</div>
                    <div class="text-caption">{{ $t('admin.activeUsers') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="6" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <v-icon color="warning" size="48">mdi-account-clock</v-icon>
                    <div class="text-h4 warning--text">{{ stats.pendingUsers }}</div>
                    <div class="text-caption">{{ $t('admin.pendingUsers') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="6" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <v-icon color="info" size="48">mdi-teach</v-icon>
                    <div class="text-h4 info--text">{{ stats.instructors }}</div>
                    <div class="text-caption">{{ $t('admin.instructors') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useSnackbarStore } from '@/stores/snackbar'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'

const { t } = useI18n()
const authStore = useAuthStore()
const snackbarStore = useSnackbarStore()

// Form data
const valid = ref(false)
const loading = ref(false)
const passwordLoading = ref(false)

// Profile data
const profileData = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: ''
})

// Password data
const passwordData = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// Password visibility
const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// Stats data
const stats = ref({
  totalUsers: 0,
  activeUsers: 0,
  pendingUsers: 0,
  instructors: 0
})

// Validation rules
const emailRules = [
  v => !!v || t('validation.required'),
  v => /.+@.+\..+/.test(v) || t('validation.invalidEmail')
]

const phoneRules = [
  v => !!v || t('validation.required'),
  v => /^\+7\d{10}$/.test(v) || t('validation.invalidPhone')
]

const passwordRules = [
  v => !v || v.length >= 6 || t('validation.passwordLength')
]

const confirmPasswordRules = [
  v => !v || v === passwordData.value.newPassword || t('validation.passwordMatch')
]

// Computed
const roleOptions = computed(() => [
  { text: t('roles.ADMIN'), value: 'ADMIN' },
  { text: t('roles.INSTRUCTOR'), value: 'INSTRUCTOR' },
  { text: t('roles.THEORY_TEACHER'), value: 'THEORY_TEACHER' },
  { text: t('roles.STUDENT'), value: 'STUDENT' }
])

const canChangePassword = computed(() => {
  return passwordData.value.currentPassword && 
         passwordData.value.newPassword && 
         passwordData.value.confirmPassword &&
         passwordData.value.newPassword === passwordData.value.confirmPassword &&
         passwordData.value.newPassword.length >= 6
})

// Methods
const loadProfile = async () => {
  try {
    const user = authStore.user
    if (user) {
      profileData.value = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || ''
      }
    }
  } catch (error) {
    // Error
  snackbarStore.error(t('errors.loadProfile'))
  }
}

const loadStats = async () => {
  try {
    // Log removed
    // Log removed
    // Log removed
    
    const response = await api.get('/admin/statistics')
    stats.value = response.data
    // Log removed
  } catch (error) {
    // Error
    // Error
    // Error
    
    // Show user-friendly error
    if (error.response?.status === 401) {
  snackbarStore.error('Нет доступа к админским функциям')
    } else if (error.response?.status === 403) {
  snackbarStore.error('Недостаточно прав для просмотра статистики')
    } else {
  snackbarStore.error('Ошибка загрузки статистики')
    }
  }
}

const updateProfile = async () => {
  if (!valid.value) return
  
  loading.value = true
  try {
    const response = await api.put('/user/profile', profileData.value)
    
    // Update auth store
    await authStore.updateProfile(response.data)
    
  snackbarStore.success(t('profile.profileUpdated'))
  } catch (error) {
    // Error
  snackbarStore.error(error.response?.data?.message || t('errors.updateProfile'))
  } finally {
    loading.value = false
  }
}

const changePassword = async () => {
  if (!canChangePassword.value) return
  
  passwordLoading.value = true
  try {
    await api.put('/user/change-password', {
      currentPassword: passwordData.value.currentPassword,
      newPassword: passwordData.value.newPassword
    })
    
    // Clear password fields
    passwordData.value = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
    
  snackbarStore.success(t('profile.passwordChanged'))
  } catch (error) {
    // Error
  snackbarStore.error(error.response?.data?.message || t('errors.changePassword'))
  } finally {
    passwordLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadProfile()
  loadStats()
})
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}

.text-h4 {
  font-weight: bold;
  margin: 8px 0;
}

.text-caption {
  color: rgba(0, 0, 0, 0.6);
}
</style>